import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { ReferralSettingFormRule, ReferralSettingFormSchema } from "@factory/form/form.rule";
import { useFormDefaultReferralSetting } from "@factory/form/form-default-referral-setting.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseReferralSettingModel } from "@factory/model/query-model-referral-setting";
import { 
  useQyDeleteReferralSetting, 
  useQyGetReferralSettingById, 
  useQyUpdateReferralSetting} from "@factory/query/referral-setting.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditReferralSetting() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getUserRole } = useUserIdentityContext();
  const { getDefault } = useFormDefaultReferralSetting();
  const navigate = useNavigate();
  const { mapUpdateReferralSetting, mapDeleteReferralSetting } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { referralSettingId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID ReferralSetting
  const handleGetApiSuccess = (data: GetResponseReferralSettingModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("tenant", responseData?.tenant || "");
      setValue("points", responseData?.points);
      setValue("stamp", responseData?.stamp);
      setValue("referralType", responseData?.referral_type || "");
  };
  const {
    data: referralSettingResponse,
    isLoading,
    isError: referralSettingError,
  } = useQyGetReferralSettingById(referralSettingId || "", handleGetApiSuccess);
  const referralSettingData = referralSettingResponse?.data?.[0];

  // API UPDATE ReferralSetting
  const handleUpdateApiSuccess = () => {
    showSuccess("ReferralSetting updated successfully");
  };
  const { mutate: updateReferralSetting, isLoading: isEditLoading } = useQyUpdateReferralSetting(
    handleUpdateApiSuccess
  );

  // API DELETE ReferralSetting
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("ReferralSetting deleted successfully");
  };
  const { mutate: deleteReferralSetting } = useQyDeleteReferralSetting(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<ReferralSettingFormSchema>({
    defaultValues: getDefault(referralSettingData),
    resolver: zodResolver(ReferralSettingFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: ReferralSettingFormSchema) => {
    if (!referralSettingData || !referralSettingData.code) {
      throw new Error("No referralSetting data");
    }
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant
    } as ReferralSettingFormSchema

    const formData = mapUpdateReferralSetting(updatedForm, referralSettingData.code);
    updateReferralSetting(formData);
  };
  const handleValidateError = (err: FieldErrors<ReferralSettingFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!referralSettingData || !referralSettingData.code) {
      throw new Error("No referralSetting data found");
    }

    const form = mapDeleteReferralSetting(referralSettingData.code);
    deleteReferralSetting(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    referralSettingId,
    referralSettingData,
    isLoading,
    referralSettingError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
